import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  Stack,
  Switch,
  Textarea,
  Title,
  SelectItem,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useCheckInSave, useGoal } from "@/queries/goals.query.ts";
import {
  ApiGoalCheckIn,
  ApiGoalCheckInKeyResultsPayload,
  ApiGoalCheckInPayload,
  ApiGoalConfidenceLevel,
  ApiGoalKeyResult,
  ApiGoalMetricType,
  ApiGoalStatus,
  GoalConfidenceLevelItems,
  GoalStatusItems,
} from "@/types/goals.types.ts";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface GoalCheckInEditModalProps extends ModalProps {
  goalId: number;
  existingCheckIn?: ApiGoalCheckIn;
}

interface KeyResultValue {
  keyResultId: number;
  value: number | boolean | null;
  initialValue: number | boolean | null;
  isModified: boolean;
}

export function GoalCheckInEditModal({
  goalId,
  existingCheckIn,
  ...props
}: GoalCheckInEditModalProps) {
  const { close } = useModals();
  const { t } = useTranslation();
  const { id } = props;

  const { data: goal } = useGoal(goalId);
  const keyResults = goal?.goal_key_results ?? [];

  const checkInSave = useCheckInSave();

  const confidenceLevelOptions = GoalConfidenceLevelItems();
  const statusOptions = GoalStatusItems();

  const [keyResultValues, setKeyResultValues] = useState<KeyResultValue[]>([]);
  const isEditing = !!existingCheckIn;
  const checkInId = existingCheckIn?.result_session_id;
  const [showDateAdjustment, setShowDateAdjustment] = useState(false);
  const [dateAdjustment, setDateAdjustment] = useState<Date | null>(
    existingCheckIn?.datetime_adjustment
      ? new Date(existingCheckIn.datetime_adjustment)
      : new Date()
  );

  const booleanOptions: SelectItem[] = [
    { id: "true", title: t("common:yes") },
    { id: "false", title: t("common:no") },
  ];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      confidence_level: ApiGoalConfidenceLevel.on_track,
      status: ApiGoalStatus.active,
      comment: "",
    },
  });

  const confidenceLevel = watch("confidence_level");
  const status = watch("status");

  useEffect(() => {
    if (existingCheckIn) {
      setValue("confidence_level", existingCheckIn.current_confidence_level);
      setValue("status", existingCheckIn.current_status);
      setValue("comment", existingCheckIn.comment?.text ?? "");
    } else if (goal) {
      setValue("confidence_level", goal.confidence_level);
      setValue("status", goal.status);
    }
  }, [goal, existingCheckIn]);

  useEffect(() => {
    if (keyResults.length > 0 && keyResultValues.length === 0) {
      setKeyResultValues(
        keyResults.map((kr) => {
          const existingKr = existingCheckIn?.key_results?.find((ekr) => ekr.id === kr.id);
          const initialValue = existingKr
            ? kr.metric_type === ApiGoalMetricType.boolean
              ? (existingKr.current_value?.value_bool ?? null)
              : (existingKr.current_value?.value_float ?? null)
            : kr.metric_type === ApiGoalMetricType.boolean
              ? (kr.current_value?.value_bool ?? null)
              : (kr.current_value?.value_float ?? null);
          return {
            keyResultId: kr.id,
            value: initialValue,
            initialValue,
            isModified: false,
          };
        })
      );
    }
  }, [keyResults, keyResultValues.length, existingCheckIn]);

  const handleValueChange = useCallback((keyResultId: number, value: number | boolean | null) => {
    setKeyResultValues((prev) =>
      prev.map((s) =>
        s.keyResultId === keyResultId ? { ...s, value, isModified: value !== s.initialValue } : s
      )
    );
  }, []);

  const buildKeyResultPayload = useCallback(
    (krValue: KeyResultValue): ApiGoalCheckInKeyResultsPayload | null => {
      const keyResult = keyResults.find((kr) => kr.id === krValue.keyResultId);
      if (!keyResult) return null;

      let value;
      switch (keyResult.metric_type) {
        case ApiGoalMetricType.boolean:
          value = { value_bool: Boolean(krValue.value) };
          break;
        case ApiGoalMetricType.currency:
          value = {
            value_float: krValue.value as number,
            value_text: keyResult.start_value?.value_text ?? null,
          };
          break;
        case ApiGoalMetricType.number:
        case ApiGoalMetricType.percentage:
        default:
          value = { value_float: krValue.value as number };
          break;
      }

      return {
        key_result_id: krValue.keyResultId,
        key_result_check_in: { value },
      };
    },
    [keyResults]
  );

  const onSubmit = handleSubmit(async (data) => {
    const keyResults = existingCheckIn?.key_results.map((keyResult) => keyResult.id) ?? [];

    const filledKeyResults = keyResultValues.filter((kr) =>
      isEditing
        ? (kr.isModified && kr.value !== null) || keyResults.includes(kr.keyResultId)
        : kr.isModified && kr.value !== null
    );
    const keyResultPayloads = filledKeyResults
      .map(buildKeyResultPayload)
      .filter((p): p is ApiGoalCheckInKeyResultsPayload => p !== null);

    let datetimeAdjustment: Date | null = null;
    if (showDateAdjustment && dateAdjustment) {
      const adjustedDate = new Date(dateAdjustment);
      adjustedDate.setHours(0, 0, 0, 0);
      datetimeAdjustment = adjustedDate;
    } else if (!showDateAdjustment && isEditing && existingCheckIn?.datetime_adjustment) {
      datetimeAdjustment = new Date(existingCheckIn.datetime_adjustment);
    }

    const payload: ApiGoalCheckInPayload = {
      confidence_level: data.confidence_level as ApiGoalConfidenceLevel,
      status: data.status as ApiGoalStatus,
      comment: data.comment || null,
      key_results: keyResultPayloads,
      datetime_adjustment: datetimeAdjustment,
    };

    try {
      await checkInSave.mutateAsync({ goalId, checkInId, payload });
      close(id);
    } catch {
      return;
    }
  });

  const renderValueInput = (keyResult: ApiGoalKeyResult, krValue: KeyResultValue) => {
    switch (keyResult.metric_type) {
      case ApiGoalMetricType.boolean:
        return (
          <Select
            label={keyResult.title}
            emptyTitle={t("common:select")}
            options={booleanOptions}
            value={krValue.value === null ? undefined : String(krValue.value)}
            onChange={(v) => handleValueChange(keyResult.id, v === "true")}
          />
        );

      case ApiGoalMetricType.percentage:
        return (
          <Input
            type="number"
            min={0}
            max={100}
            label={keyResult.title}
            value={krValue.value?.toString() ?? ""}
            onChange={(e) =>
              handleValueChange(keyResult.id, e.target.value ? Number(e.target.value) : null)
            }
          />
        );

      case ApiGoalMetricType.currency:
        return (
          <Input
            type="number"
            label={`${keyResult.title} ${keyResult.start_value?.value_text ? `(${keyResult.start_value?.value_text})` : ""}`}
            value={krValue.value?.toString() ?? ""}
            onChange={(e) =>
              handleValueChange(keyResult.id, e.target.value ? Number(e.target.value) : null)
            }
          />
        );

      case ApiGoalMetricType.number:
      default:
        return (
          <Input
            type="number"
            label={keyResult.title}
            value={krValue.value?.toString() ?? ""}
            onChange={(e) =>
              handleValueChange(keyResult.id, e.target.value ? Number(e.target.value) : null)
            }
          />
        );
    }
  };

  return (
    <Modal
      title={checkInId ? t("goals:edit_update") : t("goals:new_update")}
      withCloser
      closeByEscEnabled
      {...props}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={onSubmit}
            isLoading={checkInSave.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Select
            label={t("goals:confidence_level")}
            emptyTitle={t("common:select")}
            options={confidenceLevelOptions}
            value={confidenceLevel}
            onChange={(v) => setValue("confidence_level", v as ApiGoalConfidenceLevel)}
          />

          <Select
            label={t("common:status")}
            emptyTitle={t("common:select")}
            options={statusOptions}
            value={status}
            onChange={(v) => setValue("status", v as ApiGoalStatus)}
          />

          {keyResults.length > 0 && (
            <>
              <Title size={"xs"} header={t("goals:metrics")} paddingTop />
              <Stack gap={"sm"}>
                {keyResults.map((keyResult) => {
                  const krValue = keyResultValues.find((kr) => kr.keyResultId === keyResult.id);
                  if (!krValue) return null;

                  return (
                    <Stack key={keyResult.id} gap="sm">
                      {renderValueInput(keyResult, krValue)}
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}

          <Stack className={"py-4"}>
            <Switch
              checked={showDateAdjustment}
              onChange={setShowDateAdjustment}
              title={t("goals:specify_date")}
              description={t("goals:specify_date_description")}
            />
            {showDateAdjustment && (
              <Datepicker
                label={t("common:date")}
                value={dateAdjustment}
                onChange={setDateAdjustment}
                maxDate={new Date()}
              />
            )}
          </Stack>

          <Title size={"xs"} header={t("common:comment")} paddingTop />
          <Textarea
            {...register("comment")}
            label={t("common:comment")}
            error={errors.comment?.message}
          />
        </Stack>
      </form>
    </Modal>
  );
}
